// frontend/src/pages/Onboarding.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUser, useAuth } from "@clerk/clerk-react";
import {
  getConferences,
  getStations,
  getDistricts,
  getChurches,
} from "../services/api";
import {
  User,
  Church,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Search,
  Mail,
  Phone,
  MapPin,
  Calendar,
  RefreshCcw,
  Info,
  Layers,
  ShieldCheck,
  X,
} from "lucide-react";
import logo from "../assets/voplogo1.webp";

const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env?.VITE_API_BASE?.replace(/\/+$/, "")) ||
  (process.env.REACT_APP_API_BASE &&
    process.env.REACT_APP_API_BASE.replace(/\/+$/, "")) ||
  "http://localhost:5050";

async function fetchJSON(path, token, { method = "GET", body, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  const isJson = res.headers.get("content-type")?.includes("application/json");
  const data = isJson ? await res.json().catch(() => ({})) : {};

  if (!res.ok || data?.ok === false) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`;
    throw new Error(msg);
  }

  return data?.data ?? data;
}

async function getMe(token, signal) {
  return fetchJSON("/api/users/me", token, { method: "GET", signal });
}

async function getOnboardingStatus(token, signal) {
  return fetchJSON("/api/onboarding/status", token, { method: "GET", signal });
}

async function completeOnboarding(payload, token) {
  return fetchJSON("/api/onboarding/complete", token, {
    method: "POST",
    body: payload,
  });
}

function isAdminRole(role) {
  const r = String(role || "").toLowerCase();
  return ["admin", "union_admin", "super_admin", "conference_admin", "district_admin"].includes(r);
}

function formatHierarchyLine(ch) {
  // ✅ ORDER: Conference, Station, District (as requested)
  const conf = ch?.conferenceName || "—";
  const st = ch?.stationName || "—";
  const dist = ch?.districtName || "—";
  return `${conf} • ${st} • ${dist}`;
}

export default function Onboarding() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);

  // Personal info state
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [gender, setGender] = useState(null);

  // Step 2 tab
  const [churchTab, setChurchTab] = useState("search");

  // Church selection state
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedChurch, setSelectedChurch] = useState(null);
  const [noChurchSelected, setNoChurchSelected] = useState(false);

  // ✅ Live suggestions (mobile-friendly dropdown)
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Browse selection
  const [selectedConfId, setSelectedConfId] = useState("");
  const [selectedStationId, setSelectedStationId] = useState("");
  const [selectedDistrictId, setSelectedDistrictId] = useState("");

  // UX state
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loadingDirs, setLoadingDirs] = useState(true);
  const [dirHint, setDirHint] = useState("");
  const [initializing, setInitializing] = useState(true);

  // Live hierarchy caches
  const [confs, setConfs] = useState([]);
  const [stats, setStats] = useState([]);
  const [dists, setDists] = useState([]);
  const [chs, setChs] = useState([]);

  // Prefill from Clerk (name/email only)
  useEffect(() => {
    if (user) {
      setFullName(user.fullName || "");
      setEmail(
        (user.emailAddresses && user.emailAddresses[0]?.emailAddress) ||
          user.primaryEmailAddress?.emailAddress ||
          ""
      );
    }
  }, [user]);

  // ✅ Server-side gate: onboarding is ONLY for learners and only once
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      try {
        if (!user) {
          setInitializing(false);
          return;
        }

        const token = await getToken().catch(() => null);
        if (!token) {
          setInitializing(false);
          return;
        }

        // 1) Resolve role from DB
        const me = await getMe(token, controller.signal);
        const role = String(me?.role || "learner").toLowerCase();

        // Admins/instructors should never be here
        if (isAdminRole(role) || role === "instructor") {
          navigate("/role-redirect", { replace: true });
          return;
        }

        // 2) Check onboarding status from server
        const st = await getOnboardingStatus(token, controller.signal);
        const { onboardingRequired, onboardingComplete } = st || {};

        if (onboardingRequired === false || onboardingComplete === true) {
          navigate("/role-redirect", { replace: true });
          return;
        }
      } catch (err) {
        console.error("[onboarding] server gate failed:", err);
      } finally {
        if (!cancelled) setInitializing(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [user, getToken, navigate]);

  /* -----------------------------------------------------------------------
     Lookup helpers
  ----------------------------------------------------------------------- */

  const confById = useMemo(() => {
    const m = new Map();
    confs.forEach((c) => m.set(String(c.id), c));
    return m;
  }, [confs]);

  const stationById = useMemo(() => {
    const m = new Map();
    stats.forEach((s) => m.set(String(s.id), s));
    return m;
  }, [stats]);

  const distById = useMemo(() => {
    const m = new Map();
    dists.forEach((d) => m.set(String(d.id), d));
    return m;
  }, [dists]);

  const enrichedChurches = useMemo(() => {
    return chs.map((c) => {
      const dist = distById.get(String(c.district_id));
      const st = dist ? stationById.get(String(dist.station_id)) : null;
      const conf = st ? confById.get(String(st.conf_id)) : null;
      return {
        churchId: String(c.id),
        districtId: dist ? String(dist.id) : "",
        stationId: st ? String(st.id) : "",
        conferenceId: conf ? String(conf.id) : "",

        id: String(c.id),
        name: c.name,
        districtName: dist?.name || "",
        stationName: st?.name || "",
        conferenceName: conf?.abbrev || conf?.name || "",
      };
    });
  }, [chs, distById, stationById, confById]);

  const loadDirectory = useCallback(async () => {
    try {
      setDirHint("");
      setLoadingDirs(true);
      const [C, S, D, Ch] = await Promise.all([
        getConferences(),
        getStations(),
        getDistricts(),
        getChurches(),
      ]);
      setConfs(C || []);
      setStats(S || []);
      setDists(D || []);
      setChs(Ch || []);

      if (!Ch?.length) {
        setDirHint(
          "No churches received. Confirm /api/hierarchy/church returns { ok:true, data:[...] } and your frontend unwrap is correct."
        );
      } else if (!S?.length || !D?.length || !C?.length) {
        setDirHint(
          "Some hierarchy lists are empty. Confirm /api/hierarchy/conference|station|district return arrays."
        );
      }
    } catch (e) {
      console.error(e);
      setError("Could not load SDA directory. Check your connection or backend.");
    } finally {
      setLoadingDirs(false);
    }
  }, []);

  useEffect(() => {
    loadDirectory();
  }, [loadDirectory]);

  function nextStep() {
    setError("");

    if (step === 1) {
      if (!fullName.trim()) return setError("Please enter your full name.");
      if (!email.trim()) return setError("Please enter your email address.");
      if (!phone.trim()) return setError("Please enter your phone number.");
      if (!dateOfBirth) return setError("Please enter your date of birth.");
      if (!gender) return setError("Please select your gender.");
    }

    if (step === 2) {
      if (noChurchSelected) {
        // ok
      } else if (!selectedChurch) {
        return setError("Please select your church (or choose 'No church').");
      }
    }

    setStep((s) => Math.min(3, s + 1));
  }

  function prevStep() {
    setError("");
    setStep((s) => Math.max(1, s - 1));
  }

  function resetChurchSelection() {
    setSelectedChurch(null);
    setNoChurchSelected(false);

    setSelectedConfId("");
    setSelectedStationId("");
    setSelectedDistrictId("");

    setQuery("");
    setSearchResults([]);
    setSuggestions([]);
    setShowSuggestions(false);
  }

  function pickChurch(c) {
    setSelectedChurch(c);
    setNoChurchSelected(false);

    setSelectedConfId(c.conferenceId || "");
    setSelectedStationId(c.stationId || "");
    setSelectedDistrictId(c.districtId || "");

    setQuery(c.name || "");
    setError("");
    setShowSuggestions(false);
  }

  function runChurchSearch() {
    setError("");
    setNoChurchSelected(false);
    setSelectedChurch(null);

    const q = query.trim().toLowerCase();
    if (!q) {
      setSearchResults([]);
      return;
    }

    // Use current suggestions if available (fast + consistent)
    const fallback = enrichedChurches
      .filter((c) => {
        const name = (c.name || "").toLowerCase();
        const district = (c.districtName || "").toLowerCase();
        const station = (c.stationName || "").toLowerCase();
        const conf = (c.conferenceName || "").toLowerCase();
        return `${name} ${district} ${station} ${conf}`.includes(q);
      })
      .slice(0, 24);

    setSearchResults((suggestions?.length ? suggestions : fallback) || []);
    setShowSuggestions(false);
  }

  // ✅ Suggestions while typing (debounced)
  useEffect(() => {
    if (churchTab !== "search") return;

    const q = query.trim().toLowerCase();
    if (!q) {
      setSuggestions([]);
      return;
    }

    const t = setTimeout(() => {
      const res = enrichedChurches
        .filter((c) => {
          const name = (c.name || "").toLowerCase();
          const district = (c.districtName || "").toLowerCase();
          const station = (c.stationName || "").toLowerCase();
          const conf = (c.conferenceName || "").toLowerCase();
          return `${name} ${conf} ${station} ${district}`.includes(q);
        })
        .slice(0, 12);

      setSuggestions(res);
    }, 180);

    return () => clearTimeout(t);
  }, [query, enrichedChurches, churchTab]);

  const stationsForConf = useMemo(() => {
    if (!selectedConfId) return [];
    return stats.filter((s) => String(s.conf_id) === String(selectedConfId));
  }, [stats, selectedConfId]);

  const districtsForStation = useMemo(() => {
    if (!selectedStationId) return [];
    return dists.filter((d) => String(d.station_id) === String(selectedStationId));
  }, [dists, selectedStationId]);

  const churchesForDistrict = useMemo(() => {
    if (!selectedDistrictId) return [];
    return enrichedChurches
      .filter((c) => String(c.districtId) === String(selectedDistrictId))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [enrichedChurches, selectedDistrictId]);

  const selectedChurchSummary = useMemo(() => {
    if (noChurchSelected) {
      return {
        title: "No church / Not SDA",
        subtitle: "Instructor will be assigned by Voice of Prophecy.",
      };
    }
    if (!selectedChurch) return null;
    return {
      title: selectedChurch.name,
      // ✅ ORDER: Conference, Station, District
      subtitle: formatHierarchyLine(selectedChurch),
    };
  }, [selectedChurch, noChurchSelected]);

  async function handleConfirm() {
    setError("");
    setSaving(true);

    try {
      const token = await getToken();
      if (!token) throw new Error("No session token. Please sign in again.");

      const payloadBase = {
        fullName: fullName.trim(),
        email: email?.trim() || null,
        phone: phone.trim(),
        dob: dateOfBirth || null,
        gender,
      };

      if (noChurchSelected) {
        await completeOnboarding(
          {
            ...payloadBase,
            noChurch: true,
            conferenceId: null,
            stationId: null,
            districtId: null,
            churchId: null,
          },
          token
        );
      } else {
        if (
          !selectedChurch?.churchId ||
          !selectedChurch?.districtId ||
          !selectedChurch?.stationId ||
          !selectedChurch?.conferenceId
        ) {
          throw new Error("Please select a valid church from the list.");
        }

        await completeOnboarding(
          {
            ...payloadBase,
            noChurch: false,
            conferenceId: selectedChurch.conferenceId,
            stationId: selectedChurch.stationId,
            districtId: selectedChurch.districtId,
            churchId: selectedChurch.churchId,
          },
          token
        );
      }

      setSuccess(true);
      setTimeout(() => navigate("/role-redirect", { replace: true }), 900);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to save onboarding. Try again.");
    } finally {
      setSaving(false);
    }
  }

  const steps = [
    { number: 1, label: "Personal Info", icon: User },
    { number: 2, label: "Select Church", icon: Church },
    { number: 3, label: "Confirm", icon: CheckCircle2 },
  ];

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-14 w-14 border-4 border-orange-200 border-t-orange-600 mx-auto mb-4" />
          <p className="text-gray-700 font-semibold">Checking your profile…</p>
          <p className="text-gray-500 text-sm mt-2">This usually takes a moment.</p>
        </div>
      </div>
    );
  }

  const TabButton = ({ active, icon: Icon, title, subtitle, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-2xl border p-4 md:p-5 transition-all w-full ${
        active
          ? "border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 shadow-sm"
          : "border-gray-200 bg-white hover:bg-gray-50"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
            active
              ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white"
              : "bg-gray-100 text-gray-700"
          }`}
        >
          <Icon size={18} />
        </div>
        <div className="flex-1">
          <div className={`font-bold ${active ? "text-orange-700" : "text-gray-900"}`}>
            {title}
          </div>
          <div className="text-sm text-gray-600 mt-0.5">{subtitle}</div>
        </div>
      </div>
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-white relative overflow-hidden">
      {/* background orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-orange-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 py-6 px-4 md:px-8 border-b border-orange-100 bg-white/80 backdrop-blur-lg">
        <div className="flex items-center gap-3">
          <div className="relative group">
            <div className="absolute -inset-2 bg-gradient-to-r from-orange-400 to-amber-400 rounded-full blur opacity-25 group-hover:opacity-40 transition duration-500"></div>
            <img src={logo} alt="VOP" className="relative h-12 w-auto rounded-md drop-shadow" />
          </div>
          <div className="flex flex-col">
            <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
              Voice Of Prophecy Virtual School
            </span>
            <span className="text-xs text-gray-600 font-medium">
              Welcome! Let&apos;s get you started
            </span>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="relative z-10 flex flex-col items-center px-4 py-10 md:py-12">
        <div className="w-full max-w-4xl">
          {/* Progress steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between relative">
              <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200 -z-10"></div>
              <div
                className="absolute top-5 left-0 h-1 bg-gradient-to-r from-orange-500 to-amber-500 transition-all duration-500 -z-10"
                style={{ width: `${((step - 1) / 2) * 100}%` }}
              ></div>

              {steps.map((s) => {
                const Icon = s.icon;
                const isActive = step >= s.number;
                const isCurrent = step === s.number;

                return (
                  <div key={s.number} className="flex flex-col items-center relative">
                    <div
                      className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-500 ${
                        isActive
                          ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg scale-110"
                          : "bg-white border-2 border-gray-300 text-gray-400"
                      }`}
                    >
                      <Icon size={20} />
                    </div>
                    <div className="absolute -bottom-8 text-center whitespace-nowrap">
                      <div
                        className={`text-sm font-medium ${
                          isCurrent
                            ? "text-orange-600"
                            : isActive
                            ? "text-gray-700"
                            : "text-gray-400"
                        }`}
                      >
                        {s.label}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Card */}
          <div className="bg-white/90 backdrop-blur-xl border border-orange-100 rounded-3xl shadow-2xl p-6 sm:p-8 md:p-12 transition-all duration-300 hover:shadow-orange-200/50">
            {/* Step 1 */}
            {step === 1 && (
              <section className="space-y-6 animate-fadeIn">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl mb-4 shadow-lg">
                    <User className="text-white" size={32} />
                  </div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-2">
                    Tell us about yourself
                  </h2>
                  <p className="text-gray-600">We&apos;ll prefill any info we already have from your account</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <label className="flex flex-col">
                    <span className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <User size={16} className="text-orange-500" />
                      Full Name *
                    </span>
                    <input
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full bg-white border-2 border-gray-200 px-4 py-3 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-100 focus:border-orange-400 text-gray-800 transition-all"
                      placeholder="John Doe"
                    />
                  </label>

                  <label className="flex flex-col">
                    <span className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Mail size={16} className="text-orange-500" />
                      Email Address *
                    </span>
                    <input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-white border-2 border-gray-200 px-4 py-3 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-100 focus:border-orange-400 text-gray-800 transition-all"
                      placeholder="john@example.com"
                    />
                  </label>

                  <label className="flex flex-col">
                    <span className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Phone size={16} className="text-orange-500" />
                      Phone Number *
                    </span>
                    <input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full bg-white border-2 border-gray-200 px-4 py-3 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-100 focus:border-orange-400 text-gray-800 transition-all"
                      placeholder="+254 7xx xxx xxx"
                    />
                  </label>

                  <label className="flex flex-col">
                    <span className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Calendar size={16} className="text-orange-500" />
                      Date of Birth *
                    </span>
                    <input
                      type="date"
                      value={dateOfBirth}
                      onChange={(e) => setDateOfBirth(e.target.value)}
                      max={new Date().toISOString().split("T")[0]}
                      className="w-full bg-white border-2 border-gray-200 px-4 py-3 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-100 focus:border-orange-400 text-gray-800 transition-all"
                    />
                  </label>

                  <div className="md:col-span-2">
                    <span className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <User size={16} className="text-orange-500" />
                      Gender *
                    </span>
                    <div className="flex gap-4 mt-3">
                      <button
                        type="button"
                        onClick={() => setGender("male")}
                        className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                          gender === "male"
                            ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg scale-[1.02]"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        Male
                      </button>
                      <button
                        type="button"
                        onClick={() => setGender("female")}
                        className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                          gender === "female"
                            ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg scale-[1.02]"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        Female
                      </button>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <section className="space-y-6 animate-fadeIn">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl mb-4 shadow-lg">
                    <Church className="text-white" size={32} />
                  </div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-2">
                    Select your church
                  </h2>
                  <p className="text-gray-600">Choose the option that&apos;s easiest for you.</p>
                </div>

                {loadingDirs ? (
                  <div className="p-6 text-center text-gray-600">
                    <div className="inline-flex items-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-orange-200 border-t-orange-600" />
                      Loading directory…
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid md:grid-cols-3 gap-3">
                      <TabButton
                        active={churchTab === "search"}
                        icon={Search}
                        title="Search church"
                        subtitle="Type a name and pick from suggestions"
                        onClick={() => {
                          setChurchTab("search");
                          setError("");
                          setShowSuggestions(false);
                        }}
                      />
                      <TabButton
                        active={churchTab === "browse"}
                        icon={Layers}
                        title="Browse by hierarchy"
                        subtitle="Conference → Station → District → Church"
                        onClick={() => {
                          setChurchTab("browse");
                          setError("");
                          setShowSuggestions(false);
                        }}
                      />
                      <TabButton
                        active={churchTab === "none"}
                        icon={ShieldCheck}
                        title="No church / Not SDA"
                        subtitle="VOP will assign an instructor"
                        onClick={() => {
                          setChurchTab("none");
                          setError("");
                          setShowSuggestions(false);
                        }}
                      />
                    </div>

                    <div className="mt-4">
                      {churchTab === "search" && (
                        <div className="space-y-4">
                          <div className="rounded-2xl border border-gray-200 bg-white p-5">
                            <div className="flex flex-col sm:flex-row gap-3">
                              <div className="relative flex-1 min-w-0">
                                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                                  <Search size={18} />
                                </div>

                                {query?.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setQuery("");
                                      setSuggestions([]);
                                      setSearchResults([]);
                                      setShowSuggestions(false);
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                    aria-label="Clear"
                                  >
                                    <X size={16} />
                                  </button>
                                )}

                                <input
                                  value={query}
                                  onChange={(e) => {
                                    setQuery(e.target.value);
                                    setShowSuggestions(true);
                                  }}
                                  onFocus={() => setShowSuggestions(true)}
                                  onBlur={() => {
                                    // allow click selection without collapsing immediately
                                    setTimeout(() => setShowSuggestions(false), 180);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      runChurchSearch();
                                    }
                                  }}
                                  className="w-full bg-white border-2 border-gray-200 pl-11 pr-11 py-3.5 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-100 focus:border-orange-400 text-gray-800 transition-all"
                                  placeholder="Search church name (e.g. Gachororo)"
                                  autoComplete="off"
                                />

                                {/* ✅ Mobile-friendly suggestion dropdown (no overlap) */}
                                {showSuggestions && query.trim() && suggestions.length > 0 && (
                                  <div className="absolute z-50 mt-2 w-full rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
                                    <div className="px-4 py-2 border-b border-gray-100 text-xs font-semibold text-gray-600 bg-white">
                                      Suggestions
                                    </div>
                                    <div className="max-h-[50vh] overflow-auto overscroll-contain">
                                      {suggestions.map((c) => (
                                        <button
                                          key={`s-${c.id}`}
                                          type="button"
                                          onMouseDown={(e) => e.preventDefault()}
                                          onClick={() => pickChurch(c)}
                                          className="w-full text-left px-4 py-3 hover:bg-orange-50 transition-all border-b border-gray-100 last:border-0 flex items-start gap-3"
                                        >
                                          <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
                                            <Church size={18} className="text-orange-600" />
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="text-gray-900 font-bold truncate">{c.name}</div>
                                            <div className="text-sm text-gray-600 mt-0.5 truncate">
                                              {formatHierarchyLine(c)}
                                            </div>
                                          </div>
                                        </button>
                                      ))}
                                    </div>
                                    <div className="px-4 py-3 border-t border-gray-100 bg-white flex items-center justify-between gap-3">
                                      <div className="text-xs text-gray-500">
                                        Press <b>Enter</b> to see more results
                                      </div>
                                      <button
                                        type="button"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={runChurchSearch}
                                        className="text-sm font-semibold text-orange-700 hover:text-orange-800"
                                      >
                                        View results →
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>

                              <button
                                type="button"
                                onClick={runChurchSearch}
                                className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold hover:from-orange-600 hover:to-amber-600 shadow-lg hover:shadow-xl transition-all"
                              >
                                <Search size={18} />
                                Search
                              </button>
                            </div>

                            <div className="mt-3 text-xs text-gray-500 flex items-center gap-2">
                              <Info size={14} className="text-gray-400" />
                              Each church shows <b>Conference • Station • District</b> to avoid duplicates.
                            </div>
                          </div>

                          <div className="rounded-2xl border border-orange-100 bg-white overflow-hidden shadow-sm">
                            <div className="px-5 py-3 border-b border-gray-100 text-sm font-semibold text-gray-800">
                              Results
                            </div>

                            {!query.trim() ? (
                              <div className="p-6 text-gray-500 text-sm flex items-center gap-2">
                                <MapPin size={16} className="text-gray-400" />
                                Start typing to see suggestions, or tap <b>Search</b>.
                              </div>
                            ) : searchResults.length === 0 ? (
                              <div className="p-6 text-gray-500 text-sm">
                                No matches found. Try another spelling or use <b>Browse by hierarchy</b>.
                              </div>
                            ) : (
                              <div className="max-h-[55vh] sm:max-h-80 overflow-auto overscroll-contain">
                                {searchResults.map((c) => (
                                  <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => pickChurch(c)}
                                    className="w-full text-left px-5 py-4 hover:bg-orange-50 transition-all border-b border-gray-100 last:border-0 flex items-start gap-3"
                                  >
                                    <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
                                      <Church size={18} className="text-orange-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-gray-900 font-bold truncate">{c.name}</div>
                                      <div className="text-sm text-gray-600 mt-0.5 truncate">
                                        {formatHierarchyLine(c)}
                                      </div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {churchTab === "browse" && (
                        <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 space-y-4">
                          {/* ✅ Mobile-friendly: stack on small screens, no overflow */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <label className="flex flex-col min-w-0">
                              <span className="text-sm font-semibold text-gray-700 mb-2">Conference</span>
                              <select
                                value={selectedConfId}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setSelectedConfId(v);
                                  setSelectedStationId("");
                                  setSelectedDistrictId("");
                                  setSelectedChurch(null);
                                  setNoChurchSelected(false);
                                  setError("");
                                }}
                                className="w-full bg-white border-2 border-gray-200 px-4 py-3 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-100 focus:border-orange-400 text-gray-800 transition-all"
                              >
                                <option value="">Select conference…</option>
                                {confs
                                  .slice()
                                  .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                                  .map((c) => (
                                    <option key={c.id} value={String(c.id)}>
                                      {c.abbrev ? `${c.abbrev} — ${c.name}` : c.name}
                                    </option>
                                  ))}
                              </select>
                            </label>

                            <label className="flex flex-col min-w-0">
                              <span className="text-sm font-semibold text-gray-700 mb-2">Station</span>
                              <select
                                value={selectedStationId}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setSelectedStationId(v);
                                  setSelectedDistrictId("");
                                  setSelectedChurch(null);
                                  setNoChurchSelected(false);
                                  setError("");
                                }}
                                disabled={!selectedConfId}
                                className="w-full bg-white border-2 border-gray-200 px-4 py-3 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-100 focus:border-orange-400 text-gray-800 transition-all disabled:bg-gray-100 disabled:text-gray-500"
                              >
                                <option value="">
                                  {selectedConfId ? "Select station…" : "Select conference first…"}
                                </option>
                                {stationsForConf
                                  .slice()
                                  .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                                  .map((s) => (
                                    <option key={s.id} value={String(s.id)}>
                                      {s.name}
                                    </option>
                                  ))}
                              </select>
                            </label>

                            <label className="flex flex-col min-w-0">
                              <span className="text-sm font-semibold text-gray-700 mb-2">District</span>
                              <select
                                value={selectedDistrictId}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setSelectedDistrictId(v);
                                  setSelectedChurch(null);
                                  setNoChurchSelected(false);
                                  setError("");
                                }}
                                disabled={!selectedStationId}
                                className="w-full bg-white border-2 border-gray-200 px-4 py-3 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-100 focus:border-orange-400 text-gray-800 transition-all disabled:bg-gray-100 disabled:text-gray-500"
                              >
                                <option value="">
                                  {selectedStationId ? "Select district…" : "Select station first…"}
                                </option>
                                {districtsForStation
                                  .slice()
                                  .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                                  .map((d) => (
                                    <option key={d.id} value={String(d.id)}>
                                      {d.name}
                                    </option>
                                  ))}
                              </select>
                            </label>

                            <label className="flex flex-col min-w-0">
                              <span className="text-sm font-semibold text-gray-700 mb-2">Church</span>
                              <select
                                value={selectedChurch?.churchId || ""}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const found = churchesForDistrict.find(
                                    (c) => String(c.churchId) === String(v)
                                  );
                                  if (found) {
                                    pickChurch(found);
                                    setSearchResults([]);
                                    setSuggestions([]);
                                  } else {
                                    setSelectedChurch(null);
                                  }
                                  setError("");
                                }}
                                disabled={!selectedDistrictId}
                                className="w-full bg-white border-2 border-gray-200 px-4 py-3 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-100 focus:border-orange-400 text-gray-800 transition-all disabled:bg-gray-100 disabled:text-gray-500"
                              >
                                <option value="">
                                  {selectedDistrictId
                                    ? `Select church… (${churchesForDistrict.length})`
                                    : "Select district first…"}
                                </option>
                                {churchesForDistrict.map((c) => (
                                  <option key={c.churchId} value={String(c.churchId)}>
                                    {c.name}
                                  </option>
                                ))}
                              </select>

                              {/* ✅ Small helper line (mobile clarity) */}
                              {selectedDistrictId ? (
                                <div className="text-xs text-gray-500 mt-2">
                                  Tip: if the list is long, use <b>Search church</b> tab for faster selection.
                                </div>
                              ) : null}
                            </label>
                          </div>

                          <div className="text-xs text-gray-500 flex items-center gap-2">
                            <Info size={14} className="text-gray-400" />
                            Browsing helps avoid duplicates.
                          </div>
                        </div>
                      )}

                      {churchTab === "none" && (
                        <div className="rounded-2xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 p-6">
                          <div className="flex items-start gap-3">
                            <div className="w-11 h-11 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center shrink-0">
                              <ShieldCheck size={20} className="text-white" />
                            </div>
                            <div className="flex-1">
                              <div className="text-gray-900 font-bold text-lg">No church / Not SDA</div>
                              <div className="text-sm text-gray-700 mt-1">
                                Voice of Prophecy will assign you an instructor.
                              </div>

                              <button
                                type="button"
                                onClick={() => {
                                  resetChurchSelection();
                                  setNoChurchSelected(true);
                                  setSelectedChurch(null);
                                  setError("");
                                }}
                                className={`mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-all ${
                                  noChurchSelected
                                    ? "bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg"
                                    : "bg-white border border-orange-200 text-orange-700 hover:bg-orange-100"
                                }`}
                              >
                                <CheckCircle2 size={18} />
                                {noChurchSelected ? "Selected" : "Continue without church"}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {(selectedChurch || noChurchSelected) && (
                      <div className="mt-6 p-6 bg-white rounded-2xl border border-orange-200 shadow-sm">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shrink-0">
                              <CheckCircle2 className="text-white" size={20} />
                            </div>
                            <div>
                              <div className="text-sm text-orange-600 font-semibold mb-1">Selected</div>
                              <div className="text-gray-900 font-bold text-lg">
                                {selectedChurchSummary?.title}
                              </div>
                              {selectedChurchSummary?.subtitle && (
                                <div className="text-sm text-gray-600 mt-1">
                                  {selectedChurchSummary.subtitle}
                                </div>
                              )}
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={resetChurchSelection}
                            className="text-sm font-semibold text-gray-600 hover:text-orange-700"
                          >
                            Change
                          </button>
                        </div>
                      </div>
                    )}

                    {dirHint && (
                      <div className="mt-4 rounded-2xl border border-yellow-200 bg-yellow-50 px-5 py-4 text-sm text-yellow-800">
                        {dirHint}
                      </div>
                    )}
                  </>
                )}

                <div className="pt-2">
                  <button
                    type="button"
                    onClick={loadDirectory}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-orange-700"
                  >
                    <RefreshCcw size={16} />
                    Reload directory
                  </button>
                </div>
              </section>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <section className="space-y-6 animate-fadeIn">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl mb-4 shadow-lg">
                    <CheckCircle2 className="text-white" size={32} />
                  </div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-2">
                    Confirm your details
                  </h2>
                  <p className="text-gray-600">Review your information before continuing</p>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div className="rounded-2xl border border-gray-200 bg-white p-5">
                    <div className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <User size={16} className="text-orange-500" /> Personal Info
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between gap-4">
                        <span className="text-gray-500">Full name</span>
                        <span className="font-semibold text-gray-900 text-right">{fullName || "—"}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-gray-500">Email</span>
                        <span className="font-semibold text-gray-900 text-right">{email || "—"}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-gray-500">Phone</span>
                        <span className="font-semibold text-gray-900 text-right">{phone || "—"}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-gray-500">DOB</span>
                        <span className="font-semibold text-gray-900 text-right">{dateOfBirth || "—"}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-gray-500">Gender</span>
                        <span className="font-semibold text-gray-900 text-right">{gender || "—"}</span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 p-5">
                    <div className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <Church size={16} className="text-orange-600" /> Church / Instructor
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between gap-4">
                        <span className="text-gray-600">Selection</span>
                        <span className="font-semibold text-gray-900 text-right">
                          {selectedChurchSummary?.title || "—"}
                        </span>
                      </div>
                      <div className="text-gray-700">
                        {selectedChurchSummary?.subtitle || "Instructor will be assigned based on your selection."}
                      </div>

                      <div className="mt-4 text-xs text-gray-600 flex items-start gap-2">
                        <Info size={14} className="mt-0.5 text-gray-500" />
                        Once you finish, we’ll attach you to the right instructor for your church.
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {error && (
              <div className="mt-6 p-4 bg-red-50 border-2 border-red-200 rounded-2xl text-red-700 text-sm flex items-center gap-2">
                <span className="text-red-500">⚠️</span>
                {error}
              </div>
            )}

            {success && (
              <div className="mt-6 p-4 bg-green-50 border-2 border-green-200 rounded-2xl text-green-700 text-sm flex items-center gap-2">
                <CheckCircle2 size={18} className="text-green-500" />
                Onboarding completed successfully!
              </div>
            )}

            <div className="mt-8 flex items-center justify-between gap-4">
              <button
                onClick={prevStep}
                disabled={step === 1 || saving}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-semibold"
              >
                <ArrowLeft size={18} />
                Back
              </button>

              {step < 3 ? (
                <button
                  onClick={nextStep}
                  className="flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] font-semibold"
                >
                  Continue
                  <ArrowRight size={18} />
                </button>
              ) : (
                <button
                  onClick={handleConfirm}
                  disabled={saving || success}
                  className="flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : success ? (
                    <>
                      <CheckCircle2 size={18} />
                      Completed
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={18} />
                      Confirm & Finish
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          <div className="mt-6 text-center text-sm text-gray-600">
            Need help?{" "}
            <span className="text-orange-600 font-semibold cursor-pointer hover:text-orange-700">
              Contact support@vop.org
            </span>
          </div>
        </div>
      </main>

      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.5s ease-out; }
      `}</style>
    </div>
  );
}
